import { Fragment, useId, useState } from 'react';
import { DemoCtaLink } from '../landing/v2/DemoCtaLink';

// Only the local copy convention <em> is interpreted. Arbitrary markup is
// rendered as text; CMS answers continue through the existing MarkdownRenderer.
export function AccentText({ children }) {
  return String(children || '').split(/(<em>[\s\S]*?<\/em>[.,;:!?…]*)/g).map((part, i) =>
    part.startsWith('<em>') ? <span className='tc-accent' key={i}>{part.slice(4).replace('</em>', '')}</span> : <Fragment key={i}>{part}</Fragment>
  );
}

export function SectionHeading({ eyebrow, heading, children, center = false, split = false, action = null }) {
  const intro = <>{eyebrow && <span className='tc-eyebrow'>{eyebrow}</span>}<h2 className='tc-heading'><AccentText>{heading}</AccentText></h2></>;
  // `split` is the mockup's "section-top": heading left, lead copy or an
  // action link right, bottoms aligned. Same content, one extra layout.
  if (split) {
    return (
      <div className={children ? 'tc-section-top' : 'tc-section-top tc-section-top--action'}>
        <div>{intro}</div>
        {(children || action) && <div className='tc-section-top__aside'>{children && <div className='tc-lead'>{children}</div>}{action}</div>}
      </div>
    );
  }
  return (
    <div className={center ? 'max-w-3xl mx-auto text-center' : 'max-w-3xl'}>
      {intro}
      {children && <div className='tc-lead mt-5'>{children}</div>}
    </div>
  );
}

export function WebsiteDemoLink({ location, children = 'Book a Demo', ...props }) {
  return <DemoCtaLink ctaLocation={location} className='tc-button tc-button--primary' {...props}>{children}</DemoCtaLink>;
}

export function WebsiteAccordion({ items, renderAnswer = (item) => item.a, isDarkMode = true }) {
  const [open, setOpen] = useState(0);
  const uid = useId();
  return (
    <div className={`tc-faq ${isDarkMode ? '' : 'tc-faq--light'}`}>
      {items.map((item, i) => {
        const question = item.q || item.question;
        const triggerId = `${uid}-question-${i}`;
        const panelId = `${uid}-answer-${i}`;
        return (
          <div className='tc-faq__item' key={question}>
            <h3>
              <button type='button' id={triggerId} aria-expanded={open === i} aria-controls={panelId} className='tc-faq__trigger' onClick={() => setOpen(open === i ? -1 : i)}>
                <span>{question}</span>
                <svg className='tc-faq__icon' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden='true'><path d='m6 9 6 6 6-6' /></svg>
              </button>
            </h3>
            <div id={panelId} className='tc-faq__panel' hidden={open !== i} role='region' aria-labelledby={triggerId}>
              <div className='tc-faq__answer'>{renderAnswer(item)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function WebsiteFAQ({ id = 'faq', heading, eyebrow = 'Frequently asked questions', items, renderAnswer, isDarkMode = true, center = true }) {
  if (!items?.length) return null;
  return (
    <section id={id} className='tc-section tc-section--border'>
      <div className='tc-container tc-container--reading'>
        <SectionHeading eyebrow={eyebrow} heading={heading} center={center} />
        <div className='mt-10'><WebsiteAccordion items={items} renderAnswer={renderAnswer} isDarkMode={isDarkMode} /></div>
      </div>
    </section>
  );
}
